import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { mappingRegionTableLabelKey, mappingRegionTableLabelParts } from "@/lib/mappingRegions";
import type { RegionMappingSession } from "@/types";

interface RegionMappingStepperProps {
  regions: RegionMappingSession[];
  currentIndex: number;
}

export function RegionMappingStepper({ regions, currentIndex }: RegionMappingStepperProps) {
  const { t } = useTranslation();

  if (regions.length <= 1) return null;

  return (
    <div className="mb-4 rounded-xl border border-[var(--color-border-light)] bg-white p-4">
      <p className="text-sm font-medium text-[var(--color-text-primary)] mb-3">
        Mapping table {currentIndex + 1} of {regions.length}
      </p>
      <ol className="flex flex-wrap gap-2">
        {regions.map((region, index) => {
          const isActive = index === currentIndex;
          const isLocked = region.locked;
          const labelParts = mappingRegionTableLabelParts(region, index + 1);
          const tableLabel = t(mappingRegionTableLabelKey(labelParts), labelParts);
          return (
            <li
              key={region.regionId}
              className={cn(
                "flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium border",
                isLocked &&
                  "border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] text-[var(--color-text-tertiary)]",
                isActive &&
                  !isLocked &&
                  "border-[var(--color-brand-primary)] bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary-dark)]",
                !isActive &&
                  !isLocked &&
                  "border-[var(--color-border-light)] text-[var(--color-text-secondary)]"
              )}
            >
              <span
                className={cn(
                  "inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px]",
                  isLocked ? "bg-[var(--color-bg-tertiary)]" : "bg-white"
                )}
              >
                {isLocked ? "✓" : index + 1}
              </span>
              <span>{tableLabel}</span>
              {isLocked && <span className="text-[10px] uppercase tracking-wide">Mapped</span>}
            </li>
          );
        })}
      </ol>
      <p className="mt-3 text-xs text-[var(--color-text-tertiary)]">
        Completed regions cannot be changed. To correct mistakes in Mapping, upload the file again.
      </p>
    </div>
  );
}
